import React, { useState, useEffect } from "react";
// prettier-ignore
import { Grid, FormControlLabel, TextField, Typography, Snackbar, Popper } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import throttle from "lodash/throttle";
import parse from "autosuggest-highlight/parse";

import { getJsonApi } from "../modules/AmplifyServices";
import { Autocomplete } from "@mui/material";
import { useStyles } from "../styles/MainContainerStyles";
import {
    normalizeUnit,
    combineUnitWithAddress,
    setUnitOnAddress,
    splitUnitFromAddress,
    extractTypedUnit
} from "../utils/addressUnit";

export const ClientAddressInput = (props) => {
    const classes = useStyles();
    const [optionsGM, setOptionsGM] = React.useState([]);
    const initialSplit = splitUnitFromAddress(props.addressOneLine || "");
    const [inputValue, setInputValue] = React.useState(
        initialSplit.baseAddress
    );
    const [unit, setUnit] = useState(initialSplit.unit);
    const [place_id, setPlaceId] = useState();
    // last combined addressOneLine emitted to the parent — used to tell a
    // parent echo apart from a genuinely external address (edit flows)
    const lastEmittedAddressRef = React.useRef("");
    // raw text the user typed, captured before Google's canonical address
    // overwrites the input
    const lastTypedInputRef = React.useRef("");
    const lastPlaceDetailsRef = React.useRef(null);
    const unitDirtyRef = React.useRef(false);
    const [addressOneLine, setAddressOneLine] = React.useState();
    const [prov, setProv] = React.useState();
    const [street_number, setStreet_Number] = useState();
    const [route, setRoute] = useState("");
    const [country, setCountry] = React.useState();
    const [postalCode, setPostalCode] = useState();
    const [countryShortName, setCountryShortName] = useState();
    const [coords, setCoords] = useState();
    const [msgOpen, setMsgOpen] = useState(false);
    const [snackMsg, setSnackMsg] = useState("");
    const [isAddressFocused, setIsAddressFocused] = useState(false);
    let textFieldError = props.error ?? false;
    let autocompleteService = props.autocompleteService;
    const loaded = React.useRef(false);
    function loadScript(src, position, id) {
        if (!position) {
            return;
        }

        const script = document.createElement("script");
        script.setAttribute("async", "");
        script.setAttribute("id", id);
        script.src = src;
        position.appendChild(script);
    }
    if (typeof window !== "undefined" && !loaded.current) {
        if (!document.querySelector("#google-maps")) {
            const url =
                "https://maps.googleapis.com/maps/api/js?key=" +
                process.env.REACT_APP_GOOGLE_MAP_KEY +
                "&libraries=places&v=3.64";
            loadScript(url, document.querySelector("head"), "google-maps");
        }

        loaded.current = true;
    }
    async function getPlaceDetails() {
        const result = await getJsonApi(
            "mapdirection",
            `/placedetails/${place_id}`
        );
        if (result && result.success) {
            return result.placedetails;
        } else {
            return null;
        }
    }
    const fetch = React.useMemo(
        () =>
            throttle((input, callback) => {
                autocompleteService?.current?.getPlacePredictions(
                    input,
                    callback
                );
            }, 200),
        [autocompleteService]
    );
    useEffect(() => {
        let active = true;

        if (
            !autocompleteService?.current &&
            window?.google?.maps?.places?.AutocompleteService
        ) {
            autocompleteService.current =
                new window.google.maps.places.AutocompleteService();
        }
        if (!autocompleteService.current) {
            return undefined;
        }

        if (inputValue === "") {
            setOptionsGM([]);
            return undefined;
        }

        fetch({ input: inputValue }, (results) => {
            if (active) {
                setOptionsGM(results || []);
            }
        });

        return () => {
            active = false;
        };
    }, [inputValue, fetch]);

    useEffect(() => {
        async function getPlaceAddress() {
            try {
                const placeDetails = await getPlaceDetails();
                console.log(
                    "Get PLace details clientAddressInput",
                    placeDetails
                );
                if (placeDetails) {
                    if (
                        placeDetails.city ||
                        placeDetails.route ||
                        placeDetails.street_number ||
                        placeDetails.postal_code
                    ) {
                        // keep a typed or Google-provided unit; the visible
                        // input shows the base address, the unit lives in its
                        // own field and is combined into addressOneLine
                        const detectedUnit =
                            unit ||
                            extractTypedUnit(
                                lastTypedInputRef.current,
                                placeDetails.addressOneLine
                            ) ||
                            normalizeUnit(placeDetails.subpremise || "") ||
                            // last resort: Google's own address often spells
                            // the unit out ("#714", "flat 3", "5/12")
                            splitUnitFromAddress(placeDetails.addressOneLine)
                                .unit;
                        if (detectedUnit !== unit) setUnit(detectedUnit);
                        const combined = combineUnitWithAddress(
                            detectedUnit,
                            placeDetails.addressOneLine
                        );
                        lastPlaceDetailsRef.current = placeDetails;
                        lastEmittedAddressRef.current = combined;
                        setProv(placeDetails.state);
                        setCountry(placeDetails.country);
                        setCountryShortName(placeDetails.country_short_name);
                        setPostalCode(placeDetails.postal_code);
                        setAddressOneLine(placeDetails.addressOneLine);
                        setCoords(placeDetails.location);
                        // the box shows the address without the unit; the
                        // unit is shown in its own field beside it
                        setInputValue(
                            splitUnitFromAddress(placeDetails.addressOneLine)
                                .baseAddress
                        );
                        props.setAddress({
                            city: placeDetails.city,
                            prov: placeDetails.state,
                            country: placeDetails.country,
                            countryShortName: placeDetails.country_short_name,
                            postalCode: placeDetails.postal_code,
                            addressOneLine: combined,
                            coords: placeDetails.location,
                            route: placeDetails.route,
                            street_number: placeDetails.street_number
                        });
                        props.setPlaceId && props.setPlaceId(place_id);
                        setRoute(placeDetails.route);
                        setStreet_Number(placeDetails.street_number);
                    } else {
                        setSnackMsg(
                            "Please provide a street address for your booking."
                        );
                        setMsgOpen(true);
                        props.setPlaceId && props.setPlaceId(null);
                        props.setAddress({
                            prov: "",
                            country: "",
                            countryShortName: "",
                            postalCode: "",
                            addressOneLine: "",
                            coords: "",
                            route: "",
                            street_number: ""
                        });
                        resetAddressFields();
                    }
                } else {
                    setSnackMsg(
                        "Please provide a street address for your booking."
                    );
                    setMsgOpen(true);
                    props.setPlaceId && props.setPlaceId(null);
                    props.setAddress({
                        prov: "",
                        country: "",
                        countryShortName: "",
                        postalCode: "",
                        addressOneLine: "",
                        coords: "",
                        route: "",
                        street_number: ""
                    });
                    resetAddressFields();
                }
            } catch (e) {
                console.log("error getting place: ", e);
            }
        }
        if (place_id) getPlaceAddress();
    }, [place_id]);

    //useEffect handles when default values (props.addressOneLine) is passed through. ie. client's saved address
    useEffect(() => {
        // parent echoing back the address we just emitted must not restart
        // the prediction/auto-select cycle — that is what used to wipe a
        // typed unit and re-hit /placedetails after every selection
        if (
            props.addressOneLine &&
            props.addressOneLine === lastEmittedAddressRef.current
        ) {
            return;
        }
        if (props.addressOneLine) {
            // external address (edit flow): pull the unit out so it is not
            // re-prepended, and resolve predictions on the base address
            const { unit: savedUnit, baseAddress } = splitUnitFromAddress(
                props.addressOneLine
            );
            setUnit(savedUnit);
            setInputValue(baseAddress);
            fetch({ input: baseAddress }, (results) => {
                setOptionsGM(results || []);
                let values = { place_id: results[0]?.place_id };
                handleAddressChange(null, values);
            });
        } else {
            // Reset the fields when addressOneLine is cleared or undefined
            resetAddressFields();
        }
    }, [fetch, props.addressOneLine]);

    function resetAddressFields() {
        setPlaceId(null);
        setInputValue("");
        setUnit("");
        setProv("");
        setCountry("");
        setCountryShortName("");
        setPostalCode("");
        setAddressOneLine("");
        setCoords();
        setRoute("");
        setStreet_Number();
        lastEmittedAddressRef.current = "";
        lastTypedInputRef.current = "";
        lastPlaceDetailsRef.current = null;
        unitDirtyRef.current = false;
    }

    const PopperMy = function (props) {
        const { anchorEl, ...otherProps } = props;

        return (
            <Popper
                {...otherProps}
                anchorEl={anchorEl}
                placement="bottom"
                modifiers={[
                    {
                        name: "flip",
                        options: {
                            fallbackPlacements: ["bottom-start"]
                        }
                    }
                ]}
            />
        );
    };

    const handleAddressChange = (event, values) => {
        textFieldError = false;
        /* if (props.setAddressOneLine && values)
            props.setAddressOneLine(
                values?.description || props.addressOneLine
            ); */
        if (props.setAddressError) props.setAddressError(false);
        if (props.skipGetPlaceId && values) {
            const description = values?.description || props.addressOneLine;
            const detectedUnit =
                unit || extractTypedUnit(lastTypedInputRef.current, description);
            if (detectedUnit !== unit) setUnit(detectedUnit);
            const combined = combineUnitWithAddress(detectedUnit, description);
            lastEmittedAddressRef.current = combined;
            setInputValue(splitUnitFromAddress(description).baseAddress);
            props.setAddress({
                addressOneLine: combined
            });
            props.setPid && props.setPid(values.place_id);
        } else {
            try {
                setPlaceId(values?.place_id || null);
            } catch (err) {
                console.log("An error occurred in handleAddressChange", err);
            }
        }
    };
    const handleChange = (event) => {
        setInputValue(event.target.value);
        if (props.onClear && place_id) props.onClear();
    };

    // re-emit the combined address when the unit is edited after an address
    // has already been selected (or cleared — reverts to the base address)
    const handleUnitChange = (event) => {
        const newUnit = event.target.value;
        setUnit(newUnit);
        unitDirtyRef.current = true;
        const pd = lastPlaceDetailsRef.current;
        if (pd) {
            // setUnitOnAddress, not combine: the user is overriding whatever
            // unit the address already carried, so the old one must go
            const combined = setUnitOnAddress(newUnit, pd.addressOneLine);
            lastEmittedAddressRef.current = combined;
            props.setAddress({
                city: pd.city,
                prov: pd.state,
                country: pd.country,
                countryShortName: pd.country_short_name,
                postalCode: pd.postal_code,
                addressOneLine: combined,
                coords: pd.location,
                route: pd.route,
                street_number: pd.street_number
            });
            unitDirtyRef.current = false;
        } else if (props.skipGetPlaceId && lastEmittedAddressRef.current) {
            const combined = setUnitOnAddress(
                newUnit,
                lastEmittedAddressRef.current
            );
            lastEmittedAddressRef.current = combined;
            props.setAddress({ addressOneLine: combined });
            unitDirtyRef.current = false;
        }
    };

    // fallback: unit edited on a saved address that never went through
    // place-details in this session — resolve the base address to a place
    // so the full payload (coords etc.) can be re-emitted with the unit
    const handleUnitBlur = () => {
        if (!unitDirtyRef.current) return;
        if (lastPlaceDetailsRef.current || props.skipGetPlaceId) return;
        if (!inputValue) return;
        fetch({ input: inputValue }, (results) => {
            setOptionsGM(results || []);
            if (results?.[0]?.place_id) setPlaceId(results[0].place_id);
        });
        unitDirtyRef.current = false;
    };
    return (
        <>
            <Snackbar
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center"
                }}
                open={msgOpen}
                autoHideDuration={5000}
                onClose={() => setMsgOpen(false)}
                ContentProps={{
                    "aria-describedby": "message-id"
                }}
                message={<span id="message-id">{snackMsg}</span>}
            />
            {/* the unit field must stay OUTSIDE the FormControlLabel — a
                <label> redirects clicks to its first input (the address
                box), which would make the unit field unfocusable */}
            <div
                style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "flex-start",
                    width: props.formWidth
                        ? props.formWidth
                        : props.overrideModalDisplay
                          ? "100%"
                          : "300px",
                    margin: props.formMargin ? props.formMargin : "8px",
                    marginLeft: props.formMarginLeft
                        ? props.formMarginLeft
                        : props.overrideModalDisplay
                          ? "0px"
                          : "8px"
                }}
            >
            <FormControlLabel
                labelPlacement="top"
                style={{
                    flex: 1,
                    minWidth: 0,
                    margin: 0
                }}
                control={
                    <Autocomplete
                        id="client-address"
                        inputValue={props.inputDisplay || inputValue}
                        className={classes.autocomplete}
                        getOptionLabel={(optionGM) => optionGM.description}
                        filterOptions={(x) => x}
                        options={optionsGM}
                        classes={{
                            inputRoot: classes.inputRoot1
                        }}
                        autoComplete
                        onChange={handleAddressChange}
                        onInputChange={(event, newInputValue, reason) => {
                            if ("clear" === reason) {
                                if (props.onClear) props.onClear();
                                resetAddressFields();
                            } else if (reason === "input") {
                                lastTypedInputRef.current = newInputValue;
                                setInputValue(newInputValue);
                            }
                        }}
                        includeInputInList
                        freeSolo
                        openOnFocus
                        style={{
                            width: "100%"
                        }}
                        renderInput={(params) => (
                            <TextField
                                sx={{
                                    position: "relative",
                                    "& .MuiOutlinedInput-input": {
                                        marginRight: "20px"
                                    }
                                }}
                                inputRef={props.inputRef}
                                variant={props.variant || "outlined"}
                                {...params}
                                size={props.smallSize ? "small" : "medium"}
                                helperText={props.helperText}
                                error={textFieldError}
                                InputProps={{
                                    ...params.InputProps,
                                    ...props.InputProps,
                                    style: props.smallSize
                                        ? {
                                              padding: "5.45px",
                                              fontSize: "14px"
                                          }
                                        : {},
                                    classes: {
                                        notchedOutline:
                                            classes.notchedOutlineProviderDir
                                    }
                                }}
                                InputLabelProps={{
                                    ...params.InputLabelProps,
                                    ...props.InputLabelProps,
                                    style: props.smallSize
                                        ? {
                                              top:
                                                  inputValue || isAddressFocused
                                                      ? "2px"
                                                      : "",
                                              lineHeight:
                                                  inputValue || isAddressFocused
                                                      ? ""
                                                      : "18px",
                                              fontSize: "14px"
                                          }
                                        : {},
                                    classes: {
                                        outlined: classes.inputRoot1,
                                        focused: classes.inputRoot1
                                    }
                                }}
                                label={
                                    props?.width === "md"
                                        ? "Please enter an address"
                                        : props?.label
                                          ? props?.label
                                          : "Please enter an address for the service"
                                }
                                onFocus={() => setIsAddressFocused(true)}
                                onBlur={() => setIsAddressFocused(false)}
                                fullWidth
                                onChange={handleChange}
                            />
                        )}
                        disabled={props?.disableAddress}
                        renderOption={(props, option) => {
                            const matches =
                                option.structured_formatting
                                    .main_text_matched_substrings || [];
                            const parts = parse(
                                option.structured_formatting.main_text,
                                matches.map((match) => [
                                    match.offset,
                                    match.offset + match.length
                                ])
                            );

                            return (
                                <li sx={{ position: "relative" }} {...props}>
                                    <Grid container alignItems="center">
                                        <Grid item>
                                            <LocationOnIcon
                                                className={classes.icon}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            {parts.map((part, index) => (
                                                <span
                                                    key={index}
                                                    style={{
                                                        fontWeight:
                                                            part.highlight
                                                                ? 700
                                                                : 400
                                                    }}
                                                >
                                                    {part.text}
                                                </span>
                                            ))}

                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {
                                                    option.structured_formatting
                                                        .secondary_text
                                                }
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </li>
                            );
                        }}
                        PopperComponent={(popperProps) => (
                            <PopperMy
                                {...popperProps}
                                anchorEl={popperProps.anchorEl}
                            />
                        )}
                    />
                }
            />
                    {!props.hideUnitField && (
                        <TextField
                            id="client-address-unit"
                            label={props.unitLabel || "Unit/Apt #"}
                            value={unit}
                            onChange={handleUnitChange}
                            onBlur={handleUnitBlur}
                            variant={props.variant || "outlined"}
                            size={props.smallSize ? "small" : "medium"}
                            disabled={props?.disableAddress}
                            style={{ width: "100px", flexShrink: 0 }}
                            /* mirror the address input's paddings so both
                               boxes get the same height: the Autocomplete
                               puts 5.45px on the root and ~2.5px on the
                               inner input in smallSize mode, while a plain
                               TextField keeps its default inner padding */
                            inputProps={{
                                maxLength: 16,
                                style: props.smallSize
                                    ? {
                                          padding: "2.5px 4px",
                                          fontSize: "14px"
                                      }
                                    : {}
                            }}
                            InputProps={{
                                style: props.smallSize
                                    ? {
                                          padding: "5.45px",
                                          fontSize: "14px"
                                      }
                                    : {}
                            }}
                            InputLabelProps={{
                                style: props.smallSize
                                    ? { fontSize: "14px" }
                                    : {}
                            }}
                        />
                    )}
            </div>
        </>
    );
};
